import { com, kotlin } from '@eidu/web'
import entityApiUrl from '../baseUrl'
import requireValidPaginationParameters from '../common/requireValidPaginationParameters'
import fetchAuthorized from '../../authorization/fetchAuthorized'
import { NOT_AUTHENTICATED_MESSAGE } from '../../authorization/requireAuthContext'
import Entity = com.eidu.entity.Entity
import entityPaginatedResponseWithRelatedFromJsonString = com.eidu.entity.api.entities.entityPaginatedResponseWithRelatedFromJsonString
import EntityId = com.eidu.entity.EntityId
import KtList = kotlin.collections.KtList
import PaginatedResponseWithRelated = com.eidu.api.pagination.PaginatedResponseWithRelated
import IndexedPaginatedResponseWithRelated = com.eidu.api.pagination.IndexedPaginatedResponseWithRelated

type GetEntitiesBatchParameters = {
  ids: EntityId[]
  withRelatedEntities?: boolean
  pageIndex: number
  pageSize: number
}

export const MAX_NUM_IDS_PER_BATCH = 50

const getEntitiesBatch = async ({
  ids,
  withRelatedEntities,
  pageIndex,
  pageSize,
}: GetEntitiesBatchParameters): Promise<PaginatedResponseWithRelated<Entity>> => {
  requireValidPaginationParameters(pageIndex, pageSize)
  if (ids.length === 0) throw Error('ids must not be empty')
  if (ids.length > MAX_NUM_IDS_PER_BATCH)
    throw Error(`ids must not contain more than ${MAX_NUM_IDS_PER_BATCH} elements`)

  const encodedIds = encodeURIComponent(ids.map((it) => it.toString()).join(','))
  const related = withRelatedEntities ? 'true' : 'false'

  return fetchAuthorized(
    (authContext) =>
      `${entityApiUrl}/organizations/${authContext.organizationId}/entities?ids=${encodedIds}&related=${related}&pageIndex=${pageIndex}&pageSize=${pageSize}`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  ).then(async (response) => {
    switch (response.status) {
      case 200:
        return entityPaginatedResponseWithRelatedFromJsonString(await response.text())
      // Only after fetchAuthorized has already renewed the token and retried, so the session
      // is genuinely gone rather than momentarily stale — say so instead of rendering "no results".
      case 401:
        throw Error(NOT_AUTHENTICATED_MESSAGE)
      case 403:
        console.warn(`Got status ${response.status} from getEntitiesBatch endpoint`)
        return new IndexedPaginatedResponseWithRelated(KtList.fromJsArray([]), KtList.fromJsArray([]), 0)
      default:
        throw Error(`Unexpected response code ${response.status} from getEntitiesBatch endpoint`)
    }
  })
}

export default getEntitiesBatch
